import type { CSSProperties, ReactNode } from 'react';
import './recipeCovers.css';

/**
 * A recipe's cover in the Animate bar, drawn the way Flow's guided tutorial covers are
 * (Tarek, 2 Nov 2026: "redesign the visuals here to fit our brand… stick to artwork
 * color"): a grainy field of Artwork's violet, one white card floating on it with
 * frosted cards behind, and a glass orb wherever a picture goes.
 *
 * It rests on its finished frame, so the covers along the strip are different pictures
 * rather than one picture caught at different moments; `playing` runs the recipe's own
 * move (a rise, a pop, a drop, a type-on…) over and over with a rest. Styles and
 * keyframes: recipeCovers.css, every rule under `.rc`.
 */

type Vars = CSSProperties & Record<`--${string}`, string | number>;

/** Artwork's violet, as four pools of colour on a base (index.css `--accent` is #b28aff on dark, #7c3aed on light). */
const VIOLET = { base: '#6d32e6', pools: ['#3f14b0', '#d9c8ff', '#b28aff', '#9258ff'] };
const GRAPHITE = { base: '#2c2b29', pools: ['#46433f', '#242220', '#5a5650', '#3b3936'] };

/** Where the pools sit: different per cover, so twenty-five violet covers are not one cover twenty-five times. */
function field(id: string, palette = VIOLET): string {
  let seed = [...id].reduce((sum, ch) => sum + ch.charCodeAt(0) * 31, 7);
  const next = () => (seed = (seed * 9301 + 49297) % 233280) / 233280;
  const pools = palette.pools.map((colour) => {
    const x = Math.round(8 + next() * 84);
    const y = Math.round(6 + next() * 88);
    const r = Math.round(38 + next() * 26);
    return `radial-gradient(${r}% ${r + 8}% at ${x}% ${y}%, ${colour} 0%, transparent 72%)`;
  });
  return `${pools.join(', ')}, ${palette.base}`;
}

/** The ground every cover stands on, and the film grain over it all. */
export function CoverField({ id, graphite = false, playing = false, children, className = '' }: { id: string; graphite?: boolean; playing?: boolean; children?: ReactNode; className?: string }) {
  return (
    <span aria-hidden className={`rc ${playing ? 'is-playing' : ''} ${className}`}>
      <span className="rc-bg" style={{ background: field(id, graphite ? GRAPHITE : VIOLET) }} />
      {children}
      <span className="rc-grain" />
      <span className="rc-speck" />
    </span>
  );
}

const d = (seconds: number): Vars => ({ '--d': `${seconds}s` });

/** One white card, centred, with the frosted ones behind it. */
function Card({ w, children, style }: { w: number; children: ReactNode; style?: CSSProperties }) {
  return (
    <span className="rc-ctr">
      <span className="rc-ghost rc-top" />
      <span className="rc-ghost rc-bot" />
      <span className="rc-card" style={{ width: `${w}cqw`, ...style }}>{children}</span>
    </span>
  );
}

/** A picture: the glass orb on its soft violet backdrop. */
function Pic({ orb = '', floor = '', style }: { orb?: string; floor?: string; style?: CSSProperties }) {
  return (
    <span className="rc-pic" style={style}>
      <span className={`rc-floor ${floor}`} />
      <span className={`rc-orb ${orb}`} />
    </span>
  );
}

function Mask({ children, delay = 0, style }: { children: ReactNode; delay?: number; style?: CSSProperties }) {
  return (
    <span className="rc-mask" style={style}>
      <span className="rc-k rc-rise" style={d(delay)}>{children}</span>
    </span>
  );
}

const Row = ({ children, style }: { children: ReactNode; style?: CSSProperties }) => <span className="rc-row" style={style}>{children}</span>;
const Lines = ({ widths }: { widths: number[] }) => (
  <span className="rc-lines">{widths.map((w, i) => <i key={i} className="rc-k rc-fade" style={{ width: `${w}%`, ...d(0.25 + i * 0.08) }} />)}</span>
);

const LOOP = (
  <svg viewBox="0 0 12 12" width="1em" height="1em" className="rc-k rc-spin">
    <path d="M9.6 4.4A4 4 0 1 0 10 7M9.8 1.8v2.8H7" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** What each recipe's card shows: the recipe at work, as one idea and as few words as it takes. */
const COVERS: Readonly<Record<string, () => ReactNode>> = {
  // ── Words ──
  'headline-reveal': () => (
    <Card w={56}>
      <span className="rc-k rc-drawx rc-rule" style={d(0.5)} />
      <span className="rc-head">
        <Mask>Less, but</Mask>
        <Mask delay={0.12}><span className="rc-accent">better.</span></Mask>
      </span>
    </Card>
  ),
  'masked-title': () => (
    <Card w={58} style={{ padding: '4cqw 4.4cqw' }}>
      <span className="rc-caps">
        <Mask>Opening</Mask>
        <span className="rc-hair" />
        <Mask delay={0.14}><span className="rc-accent">Night</span></Mask>
      </span>
    </Card>
  ),
  'kinetic-words': () => (
    <span className="rc-ctr">
      <span className="rc-stage">
        {([['Type', 4, 4, 0], ['that', 26, 17, 0.14], ['moves', 14, 30, 0.28]] as const).map(([word, x, y, delay], i) => (
          <span key={word} className={`rc-k rc-pop rc-word ${i === 2 ? 'rc-accent' : ''}`} style={{ left: `${x}cqw`, top: `${y}cqw`, ...d(delay) }}>{word}</span>
        ))}
      </span>
    </span>
  ),
  'typewriter-title': () => (
    <Card w={62}>
      <span className="rc-sub" style={{ display: 'block', marginBottom: '2cqw' }}>Note</span>
      <span className="rc-field">
        <span className="rc-k rc-type">Dear reader,</span>
        <i className="rc-caret" />
      </span>
    </Card>
  ),
  // ── Pictures ──
  'product-pop': () => (
    <Card w={52}>
      <span className="rc-thumb"><Pic orb="rc-k rc-pop-orb" /></span>
      <Row><span className="rc-ttl">Glass vase</span><span className="rc-chip rc-chip-v rc-k rc-pop" style={d(0.35)}>New</span></Row>
    </Card>
  ),
  'price-drop': () => (
    <Card w={52}>
      <span className="rc-thumb"><Pic /></span>
      <Row><span className="rc-sub" style={{ textDecoration: 'line-through' }}>$48</span><span className="rc-ttl">$24</span></Row>
      <span className="rc-k rc-drop rc-tag-price" style={d(0.1)}>−50%</span>
    </Card>
  ),
  'soft-focus': () => (
    <Card w={54}>
      <span className="rc-thumb"><span className="rc-k rc-focus rc-fill-box"><Pic /></span></span>
      <Row><span className="rc-sub">Focus</span><span className="rc-track"><i className="rc-k rc-fill" /></span></Row>
    </Card>
  ),
  'slow-zoom': () => (
    <Card w={54}>
      <span className="rc-thumb">
        <span className="rc-k rc-zoom rc-fill-box"><Pic /></span>
        <span className="rc-badge rc-r">1.3×</span>
        <i className="rc-crop rc-tl" /><i className="rc-crop rc-bl" /><i className="rc-crop rc-br" />
      </span>
      <Row><span className="rc-ttl">Push in</span><span className="rc-sub">6 s</span></Row>
    </Card>
  ),
  // ── Brand ──
  'logo-sting': () => (
    <Card w={54}>
      <span className="rc-night">
        <span className="rc-k rc-pop rc-dot" style={d(0.45)} />
        <span className="rc-k rc-tracking">Atelier</span>
      </span>
      <Row style={{ justifyContent: 'flex-start', gap: '1.4cqw' }}>
        {['#7c3aed', '#d9c8ff', '#16171a'].map((colour, i) => <i key={colour} className="rc-k rc-pop rc-swatch" style={{ background: colour, ...d(0.6 + i * 0.07) }} />)}
        <span className="rc-sub" style={{ marginLeft: 'auto' }}>Brand</span>
      </Row>
    </Card>
  ),
  'story-slides': () => (
    <span className="rc-ctr">
      <span className="rc-stories">
        {[0, 1, 2].map((i) => (
          <span key={i} className="rc-k rc-slideup rc-story" style={{ '--r': `${[-5, 0, 5][i]}deg`, ...d(i * 0.14) } as Vars}>
            <span className="rc-bars">{[0, 1, 2].map((j) => <i key={j}>{j <= i ? <b className="rc-k rc-fill" style={d(i * 0.14 + j * 0.1)} /> : null}</i>)}</span>
            <span className="rc-story-pic"><Pic /></span>
            <span className="rc-story-line" />
          </span>
        ))}
      </span>
    </span>
  ),
  // ── Loops ──
  'looping-gif': () => (
    <Card w={52}>
      <span className="rc-thumb">
        <Pic orb="rc-k rc-bounce" floor="rc-k rc-squash" />
        <span className="rc-badge rc-l">{LOOP}GIF</span>
      </span>
      <Row><span className="rc-ttl">Bounce</span><span className="rc-chip rc-chip-n">Loop</span></Row>
    </Card>
  ),
  'bouncy-drop': () => (
    <Card w={52}>
      <span className="rc-thumb"><Pic orb="rc-k rc-dropin" floor="rc-k rc-landing" /></span>
      <Row><span className="rc-ttl">Drop in</span><span className="rc-chip rc-chip-v">Spring</span></Row>
    </Card>
  ),
  'neon-sign': () => (
    <Card w={54}>
      <span className="rc-night rc-neon-box"><span className="rc-k rc-neon">Open</span></span>
      <Row><span className="rc-ttl">Neon</span><span className="rc-chip rc-chip-n">Flicker</span></Row>
    </Card>
  ),
  'sticker-pile': () => (
    <span className="rc-ctr">
      <span className="rc-stage">
        <span className="rc-k rc-slap rc-sticker rc-sticker-card" style={{ left: '8cqw', top: '6cqw', '--r': '-8deg', ...d(0) } as Vars}><Pic /></span>
        <span className="rc-k rc-slap rc-sticker rc-sticker-word" style={{ left: '34cqw', top: '4cqw', '--r': '7deg', ...d(0.16) } as Vars}>Hi!</span>
        <span className="rc-k rc-slap rc-sticker rc-sticker-star" style={{ left: '40cqw', top: '24cqw', '--r': '-4deg', ...d(0.3) } as Vars}>
          <svg viewBox="0 0 100 100" fill="currentColor"><path d="M50 0C53 30 70 47 100 50 70 53 53 70 50 100 47 70 30 53 0 50 30 47 47 30 50 0Z" /></svg>
        </span>
        <span className="rc-k rc-slap rc-sticker rc-sticker-pill" style={{ left: '12cqw', top: '32cqw', '--r': '5deg', ...d(0.44) } as Vars}>Fresh</span>
      </span>
    </span>
  ),
  // ── Moods ──
  professional: () => (
    <Card w={56}>
      <Row style={{ marginTop: 0 }}><span className="rc-ttl rc-k rc-up">Quarterly report</span></Row>
      <Lines widths={[100, 86, 64]} />
      <Row><span className="rc-chip rc-chip-n rc-k rc-fade" style={d(0.5)}>Q3</span><span className="rc-sub rc-k rc-fade" style={d(0.55)}>2026</span></Row>
    </Card>
  ),
  bold: () => (
    <Card w={58} style={{ padding: '3cqw 4cqw' }}>
      <span className="rc-k rc-slam rc-bold">Bold<span className="rc-accent">.</span></span>
    </Card>
  ),
  elegant: () => (
    <Card w={56} style={{ textAlign: 'center' }}>
      <span className="rc-k rc-fade rc-serif" style={{ animationDuration: '3.4s' }}>Elegant</span>
      <span className="rc-k rc-drawx rc-rule rc-rule-c" style={d(0.6)} />
      <span className="rc-k rc-fade rc-smallcaps" style={d(0.8)}>Spring collection</span>
    </Card>
  ),
  cinematic: () => (
    <Card w={62} style={{ padding: '2.4cqw' }}>
      <span className="rc-night rc-cine">
        <i className="rc-k rc-bar-top" /><i className="rc-k rc-bar-bot" />
        <span className="rc-k rc-tracking rc-tracking-s" style={d(0.3)}>The long road</span>
      </span>
    </Card>
  ),
  energetic: () => (
    <span className="rc-ctr">
      <span className="rc-stage">
        {[0, 1, 2].map((i) => <i key={i} className="rc-k rc-whoosh rc-speed" style={{ top: `${15 + i * 6}cqw`, left: `${4 + i * 3}cqw`, ...d(i * 0.05) }} />)}
        <span className="rc-k rc-zip rc-word rc-go" style={{ left: '20cqw', top: '10cqw', '--r': '-6deg' } as Vars}>Go<span className="rc-accent">!</span></span>
        <span className="rc-k rc-pop rc-chip rc-chip-v rc-energy-chip" style={d(0.3)}>Now</span>
      </span>
    </span>
  ),
  playful: () => (
    <Card w={56}>
      <span className="rc-play-words">
        {[...'Hey there!'].map((ch, i) => <span key={i} className="rc-k rc-hop" style={{ ...d(i * 0.05), ...(i > 3 ? { color: '#7c3aed' } : {}) }}>{ch === ' ' ? ' ' : ch}</span>)}
      </span>
      <span className="rc-k rc-wiggle rc-blob" style={d(0.6)} />
    </Card>
  ),
  handmade: () => (
    <Card w={56}>
      <span className="rc-k rc-fade rc-hand">made with love</span>
      <svg viewBox="0 0 60 12" preserveAspectRatio="none" className="rc-scribble">
        <path className="rc-k rc-draw" pathLength={1} d="M2 6c8-4 14 3 22-1s14 2 22-2 8 2 12 0" fill="none" stroke="#7c3aed" strokeWidth={2.4} strokeLinecap="round" style={d(0.4)} />
      </svg>
    </Card>
  ),
  digital: () => (
    <Card w={58}>
      <span className="rc-mono-box">
        <span className="rc-k rc-type rc-mono">LOADING_</span>
        <span className="rc-dither" />
      </span>
      <Row><span className="rc-track"><i className="rc-k rc-fill rc-steps" /></span><span className="rc-sub" style={{ fontFamily: 'var(--rc-mono)' }}>72%</span></Row>
    </Card>
  ),
  grunge: () => (
    <span className="rc-ctr">
      <span className="rc-torn rc-k rc-jolt">
        <span className="rc-grunge">Noise</span>
      </span>
    </span>
  ),
  minimal: () => (
    <Card w={50} style={{ padding: '6cqw 5cqw' }}>
      <span className="rc-k rc-fade rc-minimal" style={{ animationDuration: '3.2s' }}>Less.</span>
    </Card>
  ),
};

/** A recipe with no cover of its own (a new one) shows the Professional card until it gets one. */
export function hasCover(id: string): boolean {
  return id in COVERS;
}

export function RecipeCover({ id, playing }: { id: string; playing: boolean }) {
  const draw = COVERS[id] ?? COVERS.professional!;
  return <CoverField id={id} playing={playing}>{draw()}</CoverField>;
}
